import { useCallback, useEffect, useState } from 'react';
import type { Prefs } from '../domain/types';

const PREF_KEY = 'dada.pref.v2';

const DEFAULTS: Prefs = {
  theme: 'system',
  fontScale: 'auto',
  /*
    처음 여는 사람에게는 내 공간이 기본이다. 공유는 보드가 있어야 열리고, 그 전에는
    만들기 자리(👥+)만 보인다.
  */
  space: 'me',
  lens: 'all',
  sharedTab: 'calendar',
  view: 'calendar',
  sharedView: 'calendar',
  weekStart: 'mon',
  pinCollapsed: {},
  debtsCollapsed: true,
  budgetsCollapsed: true,
  todayCollapsed: true,
  todayMoneyCollapsed: false,
  moneyCardCollapsed: false,
};

function read(): Prefs {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Prefs> & { summaryLayoutVersion?: number; calendarDefaultVersion?: number };
    // Apply the compact summary layout once; subsequent expand/collapse choices persist.
    const compact = parsed.summaryLayoutVersion === 1 ? {} : { todayCollapsed: true, budgetsCollapsed: true, debtsCollapsed: true };
    // Switch existing devices once; later explicit list/calendar choices remain independent.
    const calendar = parsed.calendarDefaultVersion === 1 ? {} : { view: 'calendar' as const, sharedView: 'calendar' as const };
    return { ...DEFAULTS, ...parsed, ...compact, ...calendar, pinCollapsed: { ...(parsed.pinCollapsed ?? {}) } };
  } catch {
    return DEFAULTS;
  }
}

/** 기기별 UI 설정. 사용자 데이터가 아니므로 Firestore 로 올리지 않는다. */
export function usePrefs() {
  const [prefs, setPrefs] = useState<Prefs>(read);

  const set = useCallback(<K extends keyof Prefs>(key: K, value: Prefs[K]) => {
    setPrefs((p) => ({ ...p, [key]: value }));
  }, []);

  useEffect(() => {
    try { localStorage.setItem(PREF_KEY, JSON.stringify({ ...prefs, summaryLayoutVersion: 1, calendarDefaultVersion: 1 })); } catch { /* 저장소 접근 불가 */ }
  }, [prefs]);

  // 'system' 이면 data-theme 을 지워 prefers-color-scheme 이 결정하게 둔다.
  useEffect(() => {
    const root = document.documentElement;
    if (prefs.theme === 'system') delete root.dataset.theme;
    else root.dataset.theme = prefs.theme;
  }, [prefs.theme]);

  // 테마와 같은 규칙. 'auto' 면 속성을 지워 html 의 font-size 를 브라우저에 맡긴다.
  useEffect(() => {
    const root = document.documentElement;
    if (prefs.fontScale === 'auto') delete root.dataset.fontScale;
    else root.dataset.fontScale = prefs.fontScale;
  }, [prefs.fontScale]);

  return { prefs, set };
}
